import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { getLocalProfilePicUrl } from '../config/imageUrls';
import {
  Mail, Play, PlusCircle, MapPin, Search as SearchIcon,
  Settings, AlertCircle, Zap, Heart,
  UserPlus, UserMinus, Clock, CheckCircle, XCircle, RefreshCw,
  User, UserCheck, Key, Calendar, AtSign, ArrowRight,
  Database, FileText, Phone, AlertTriangle, Plane, LogOut,
  Activity, BookOpen, Star, Send, Scan, MessageCircle
} from 'lucide-react';
import AutomationBanner from '../components/AutomationBanner';

/* ═══════════════════════════════════════════════
   SCRIPT TYPE CONFIG
   ═══════════════════════════════════════════════ */

const SCRIPT_TYPES = {
  detect_gmail: { label: 'Détecter', icon: SearchIcon, color: '#0ea5e9', url: '/api/gmail-scripts/detect-gmail' },
  analyze_emails: { label: 'Analyser', icon: Mail, color: '#8b5cf6', url: '/api/gmail-scripts/analyze-emails' },
  create_gmail: { label: 'Créer Gmail', icon: PlusCircle, color: '#22c55e', url: '/api/gmail-scripts/create-gmail' },
  disconnect_gmail: { label: 'Déconnecter', icon: LogOut, color: '#ef4444', url: '/api/gmail-scripts/disconnect-gmail' },
  maps_review: { label: 'Maps', icon: MapPin, color: '#f59e0b', url: '/api/gmail-scripts/maps-review' },
};

const SCRIPT_PARAMS = {
  create_gmail: [
    { key: 'first_name', label: 'Prénom', type: 'text', placeholder: 'ex: Jean' },
    { key: 'last_name', label: 'Nom', type: 'text', placeholder: 'ex: Dupont' },
    { key: 'birth_date', label: 'Date de naissance', type: 'text', placeholder: 'JJ/MM/AAAA' },
    { key: 'gender', label: 'Genre', type: 'select', options: [{ value: '', label: 'Aléatoire' }, { value: 'male', label: 'Homme' }, { value: 'female', label: 'Femme' }] },
    { key: 'delay_range', label: 'Timer entre actions', type: 'delay_range' },
  ],
  maps_review: [
    { key: 'target_email', label: 'Email cible', type: 'text', placeholder: 'email@gmail.com (optionnel)' },
  ],
};

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`
  width: 100%;
`;

const ContentGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const LeftCol = styled.div`
  display: flex; flex-direction: column; gap: 14px;
`;

const Card = styled.div`
  padding: 18px 20px; border-radius: 12px;
  background: var(--card-bg); border: 1px solid var(--card-border);
`;

const CardLabel = styled.div`
  font-size: 10px; font-weight: 600; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .5px; margin-bottom: 8px;
`;

const AccountGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 0;
`;

const AccountLeft = styled.div`
  display: flex; align-items: center; gap: 14px;
`;

const AccountAvatar = styled.div`
  width: 52px; height: 52px; border-radius: 50%;
  background: linear-gradient(135deg, #0ea5e9, #8b5cf6);
  display: flex; align-items: center; justify-content: center; color: #fff;
  font-weight: 700; font-size: 20px; flex-shrink: 0;
`;

const AccountName = styled.div`
  font-size: 15px; font-weight: 700; color: var(--text-primary);
  span { font-size: 11px; color: var(--text-muted); font-weight: 500; display: block; margin-top: 2px; }
`;

const AccountRight = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; align-content: center; justify-content: flex-end;
  padding-left: 14px; border-left: 1px solid var(--border);
`;

const DayStat = styled.div`
  display: flex; align-items: center; gap: 6px; width: calc(50% - 3px);
  padding: 4px 8px; border-radius: 8px;
  background: ${p => p.$bg || 'rgba(14,165,233,.06)'};
  .ds-val { font-size: 16px; font-weight: 800; color: ${p => p.$color || '#0ea5e9'}; font-family: 'JetBrains Mono', monospace; }
  .ds-label { font-size: 9px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; }
`;

const ScriptSelector = styled.div`
  display: flex; gap: 4px; flex-wrap: wrap;
`;

const ScriptTypeBtn = styled.button`
  display: flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 700;
  cursor: pointer; transition: all .15s; border: 1.5px solid transparent;
  background: ${p => p.$selected ? `${p.$color}18` : 'var(--surface)'};
  color: ${p => p.$selected ? p.$color : 'var(--text-muted)'};
  border-color: ${p => p.$selected ? `${p.$color}40` : 'transparent'};
  &:hover { color: ${p => p.$color}; border-color: ${p => `${p.$color}30`}; }
`;

const LaunchBtn = styled.button`
  padding: 10px 22px; border-radius: 10px; border: none;
  background: ${p => p.$color || 'var(--primary)'};
  color: #fff; font-weight: 700; font-size: 13px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  display: flex; align-items: center; gap: 6px;
  opacity: ${p => p.disabled ? 0.4 : 1};
  transition: all .15s; margin-left: auto;
  &:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
`;

const ParamSection = styled.div`
  padding: 16px 20px; border-radius: 12px;
  background: var(--card-bg); border: 1px solid var(--card-border);
`;

const ParamHeader = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .5px; margin-bottom: 12px;
`;

const ParamRow = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 10px;
  &:last-child { margin-bottom: 0; }
`;

const ParamLabel = styled.label`
  font-size: 12px; font-weight: 600; color: var(--text-secondary);
  min-width: 130px; flex-shrink: 0;
`;

const ParamInput = styled.input`
  flex: 1; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--input-bg);
  color: var(--text-primary); font-size: 12px; outline: none;
  &:focus { border-color: var(--primary); }
`;

const ParamSelect = styled.select`
  flex: 1; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--input-bg);
  color: var(--text-primary); font-size: 12px; outline: none;
  &:focus { border-color: var(--primary); }
`;

const ActivityPanel = styled.div`
  display: flex; flex-direction: column; height: 100%;
  border-radius: 12px; background: var(--card-bg);
  border: 1px solid var(--card-border); overflow: hidden;
`;

const ActivityHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  font-size: 15px; font-weight: 700; color: var(--text-primary);
`;

const ActivityCount = styled.span`
  font-size: 12px; font-weight: 600; color: var(--text-muted);
`;

const ActivityList = styled.div`
  flex: 1; overflow-y: auto; padding: 4px 0;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const ActivityItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 18px; transition: background .1s;
  background: ${p => p.$alt ? 'rgba(165,145,239,.03)' : 'transparent'};
  &:hover { background: var(--surface-hover); }
  .act-icon { flex-shrink: 0; }
  .act-text { flex: 1; line-height: 1.4; font-size: 13px; color: var(--text-secondary); }
  .act-time { font-size: 12px; color: var(--text-muted); flex-shrink: 0; font-family: 'JetBrains Mono', monospace; font-weight: 600; }
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px; color: var(--text-muted); text-align: center;
  svg { opacity: .2; margin-bottom: 12px; }
  p { font-size: 13px; }
`;

const ResultBanner = styled.div`
  padding: 10px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
  display: flex; align-items: center; gap: 8px;
  background: ${p => p.$ok ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)'};
  border: 1px solid ${p => p.$ok ? 'rgba(34,197,94,.25)' : 'rgba(239,68,68,.25)'};
  color: ${p => p.$ok ? 'var(--success)' : 'var(--error)'};
  button { margin-left: auto; background: none; border: none; cursor: pointer;
    color: inherit; font-weight: 700; font-size: 14px; }
`;

/* ═══════════════════════════════════════════════
   ACTION ICONS MAP (for activity feed)
   ═══════════════════════════════════════════════ */

const IG_ACTION_MAP = {
  'LikeAction':        { label: 'Liked post from', icon: Heart, color: '#ef4444', bg: 'rgba(239,68,68,.12)', showProfilePic: true },
  'FollowAction':      { label: 'Followed', icon: UserPlus, color: '#22c55e', bg: 'rgba(34,197,94,.12)', showProfilePic: true },
  'GetProfileAction':  { label: 'Viewed profile', icon: UserCheck, color: '#a591ef', bg: 'rgba(165,145,239,.12)', showProfilePic: true },
  'StoryWatchAction':  { label: 'Viewed story from', icon: BookOpen, color: '#f59e0b', bg: 'rgba(245,158,11,.12)', showProfilePic: true },
  'StoryLikeAction':   { label: 'Liked story from', icon: Star, color: '#ec4899', bg: 'rgba(236,72,153,.12)', showProfilePic: true },
  'UnfollowAction':    { label: 'Unfollowed', icon: UserMinus, color: '#f97316', bg: 'rgba(249,115,22,.12)', showProfilePic: true },
  'CommentAction':     { label: 'Commented on', icon: MessageCircle, color: '#3b82f6', bg: 'rgba(59,130,246,.12)', showProfilePic: true },
  'DirectMessageAction': { label: 'DM to', icon: Send, color: '#8b5cf6', bg: 'rgba(139,92,246,.12)', showProfilePic: true },
  'ScrapeAction':      { label: 'Scraped', icon: Scan, color: '#06b6d4', bg: 'rgba(6,182,212,.12)' },
};

const ACTION_ICONS = {
  script_start:       { icon: Play,          color: '#0ea5e9',  label: 'Démarrage' },
  load_data:          { icon: FileText,      color: '#8b5cf6',  label: 'Données' },
  open_gmail:         { icon: Mail,          color: '#ea4335',  label: 'Gmail' },
  navigate:           { icon: ArrowRight,    color: '#64748b',  label: 'Navigation' },
  click_button:       { icon: Zap,           color: '#f59e0b',  label: 'Clic' },
  click_next:         { icon: ArrowRight,    color: '#06b6d4',  label: 'Suivant' },
  input_firstname:    { icon: User,          color: '#22c55e',  label: 'Prénom' },
  input_lastname:     { icon: User,          color: '#22c55e',  label: 'Nom' },
  input_gender:       { icon: UserPlus,      color: '#a855f7',  label: 'Genre' },
  input_birthday:     { icon: Calendar,      color: '#f97316',  label: 'Naissance' },
  input_email:        { icon: AtSign,        color: '#0ea5e9',  label: 'Email' },
  input_password:     { icon: Key,           color: '#ef4444',  label: 'Mot de passe' },
  phone_verification: { icon: Phone,         color: '#f59e0b',  label: 'Vérification' },
  alternative_flow:   { icon: Settings,      color: '#64748b',  label: 'Configuration' },
  move_line:          { icon: FileText,      color: '#06b6d4',  label: 'Fichier' },
  save_db:            { icon: Database,      color: '#22c55e',  label: 'Base de données' },
  save_sheets:        { icon: FileText,      color: '#34a853',  label: 'Sheets' },
  airplane_mode:      { icon: Plane,         color: '#64748b',  label: 'Mode avion' },
  close_apps:         { icon: XCircle,       color: '#64748b',  label: 'Fermeture' },
  script_end:         { icon: CheckCircle,   color: '#22c55e',  label: 'Terminé' },
  error:              { icon: XCircle,       color: '#ef4444',  label: 'Erreur' },
  warning:            { icon: AlertTriangle, color: '#f59e0b',  label: 'Attention' },
  default:            { icon: Zap,           color: 'var(--text-muted)', label: 'Action' },
};

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const GmailAutomation = ({ selectedDevice, onNavigate, activeTab: currentTab }) => {
  const [selectedScript, setSelectedScript] = useState('create_gmail');
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [customParams, setCustomParams] = useState({});
  const [gmailAccount, setGmailAccount] = useState(null);
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState({ detected: 0, analyzed: 0, created: 0, maps: 0 });
  const pollRef = useRef(null);

  const deviceId = selectedDevice?.device_id;
  const deviceName = selectedDevice?.device_name || deviceId;

  // Reset params when switching script or device
  useEffect(() => { setCustomParams({}); }, [selectedScript, deviceId]);

  // Fetch Gmail account info for device from devices table
  const fetchGmailInfo = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await fetch(`/api/devices/info/${deviceId}`);
      const data = await r.json();
      if (data.gmail_account && data.gmail_account !== 'N/A') {
        setGmailAccount(data.gmail_account);
      } else {
        setGmailAccount(null);
      }
    } catch {}
  }, [deviceId]);

  // Fetch live Gmail creation logs from JSON file
  const fetchActions = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await fetch(`/api/gmail-scripts/logs/${deviceId}`);
      const data = await r.json();
      const logActions = data.actions || [];
      setActions(logActions);
      const successes = logActions.filter(a => a.action_type === 'script_end' && a.status === 'success');
      const phoneBlocks = logActions.filter(a => a.action_type === 'phone_verification' && a.status === 'error');
      setStats({
        detected: 0,
        analyzed: 0,
        created: successes.length,
        maps: phoneBlocks.length,
      });
    } catch {}
  }, [deviceId]);

  useEffect(() => {
    fetchGmailInfo();
    fetchActions();
    pollRef.current = setInterval(() => { fetchActions(); }, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchGmailInfo, fetchActions]);

  // Launch script
  const handleLaunch = async () => {
    if (!deviceId || !selectedScript || launching) return;
    setLaunching(true);
    setLaunchResult(null);
    try {
      const cfg = SCRIPT_TYPES[selectedScript];
      if (!cfg?.url) throw new Error('Script non configuré');
      const body = { device_id: deviceId, mode: 'terminal', ...customParams };
      const r = await fetch(cfg.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (data.success) {
        setLaunchResult({ ok: true, msg: data.message || 'Script lancé' });
      } else {
        setLaunchResult({ ok: false, msg: data.error || 'Erreur' });
      }
    } catch (err) {
      setLaunchResult({ ok: false, msg: err.message });
    } finally {
      setLaunching(false);
    }
  };

  const paramDefs = SCRIPT_PARAMS[selectedScript] || [];
  const scriptCfg = SCRIPT_TYPES[selectedScript];

  if (!selectedDevice) {
    return (
      <EmptyState>
        <Mail size={48} />
        <p>Sélectionnez un device dans la sidebar pour accéder à l'automatisation Gmail</p>
      </EmptyState>
    );
  }

  return (
    <Page>
      {/* Top Banner */}
      <AutomationBanner
        platform="gmail"
        deviceName={deviceName}
        isConnected={selectedDevice?.isConnected || selectedDevice?.is_online}
        onNavigate={onNavigate}
      />

      <ContentGrid>
        <LeftCol>
        {/* Launch Result */}
        {launchResult && (
          <ResultBanner $ok={launchResult.ok}>
            {launchResult.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
            {launchResult.msg}
            <button onClick={() => setLaunchResult(null)}>✕</button>
          </ResultBanner>
        )}

        {/* Account Card */}
        <Card>
          <CardLabel>Compte Gmail</CardLabel>
          <AccountGrid>
            <AccountLeft>
              <AccountAvatar>
                <Mail size={22} />
              </AccountAvatar>
              <AccountName>
                {gmailAccount || 'Non détecté'}
                <span>{deviceName}</span>
              </AccountName>
            </AccountLeft>
            <AccountRight>
              <DayStat $bg="rgba(34,197,94,.08)" $color="#22c55e">
                <CheckCircle size={12} style={{ color: '#22c55e', flexShrink: 0 }} />
                <div><div className="ds-val">{stats.created}</div><div className="ds-label">Créés</div></div>
              </DayStat>
              <DayStat $bg="rgba(239,68,68,.08)" $color="#ef4444">
                <Phone size={12} style={{ color: '#ef4444', flexShrink: 0 }} />
                <div><div className="ds-val">{stats.maps}</div><div className="ds-label">Bloqués</div></div>
              </DayStat>
              <DayStat $bg="rgba(14,165,233,.08)" $color="#0ea5e9">
                <Zap size={12} style={{ color: '#0ea5e9', flexShrink: 0 }} />
                <div><div className="ds-val">{actions.length}</div><div className="ds-label">Actions</div></div>
              </DayStat>
              <DayStat $bg="rgba(139,92,246,.08)" $color="#8b5cf6">
                <Clock size={12} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                <div><div className="ds-val">{actions.filter(a => a.status === 'running').length > 0 ? '●' : '—'}</div><div className="ds-label">Live</div></div>
              </DayStat>
            </AccountRight>
          </AccountGrid>

          {/* Script Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <ScriptSelector style={{ flex: 1 }}>
              {Object.entries(SCRIPT_TYPES).map(([id, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <ScriptTypeBtn
                    key={id}
                    $color={cfg.color}
                    $selected={selectedScript === id}
                    onClick={() => setSelectedScript(id)}
                  >
                    <Icon size={11} /> {cfg.label}
                  </ScriptTypeBtn>
                );
              })}
            </ScriptSelector>
            <LaunchBtn
              $color={scriptCfg?.color}
              disabled={!selectedScript || launching}
              onClick={handleLaunch}
            >
              <Play size={12} /> {launching ? '...' : 'Lancer'}
            </LaunchBtn>
          </div>
        </Card>

        {/* Parameters Section */}
        {paramDefs.length > 0 && (
          <ParamSection>
            <ParamHeader>
              <Settings size={13} />
              Paramètres {SCRIPT_TYPES[selectedScript]?.label || ''}
            </ParamHeader>
            {paramDefs.map(p => (
              <ParamRow key={p.key}>
                <ParamLabel>{p.label}</ParamLabel>
                {p.type === 'select' ? (
                  <ParamSelect
                    value={customParams[p.key] || ''}
                    onChange={e => setCustomParams(prev => ({ ...prev, [p.key]: e.target.value }))}
                  >
                    {p.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </ParamSelect>
                ) : p.type === 'delay_range' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                    <ParamInput
                      type="number" step="0.5" min="0.5" max="30"
                      placeholder="1.5" style={{ width: 70, flex: 'none', textAlign: 'center' }}
                      value={customParams.delay_min ?? '1.5'}
                      onChange={e => setCustomParams(prev => ({ ...prev, delay_min: e.target.value }))}
                    />
                    <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>—</span>
                    <ParamInput
                      type="number" step="0.5" min="0.5" max="30"
                      placeholder="3.0" style={{ width: 70, flex: 'none', textAlign: 'center' }}
                      value={customParams.delay_max ?? '3.0'}
                      onChange={e => setCustomParams(prev => ({ ...prev, delay_max: e.target.value }))}
                    />
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>secondes</span>
                  </div>
                ) : (
                  <ParamInput
                    type="text"
                    placeholder={p.placeholder}
                    value={customParams[p.key] || ''}
                    onChange={e => setCustomParams(prev => ({ ...prev, [p.key]: e.target.value }))}
                  />
                )}
              </ParamRow>
            ))}
          </ParamSection>
        )}
      </LeftCol>

      {/* Right Column — Activity Feed */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <ActivityPanel>
          <ActivityHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={16} style={{ color: 'var(--primary)' }} />
              Dernières actions
              {(() => {
                const allItems = [...actions];
                const latest = allItems.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
                return latest ? (
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                    — {(() => { const d = (Date.now() - new Date(latest.timestamp).getTime()) / 1000; if (d < 60) return 'à l\'instant'; if (d < 3600) return `il y a ${Math.floor(d / 60)}m`; if (d < 86400) return `il y a ${Math.floor(d / 3600)}h`; return `il y a ${Math.floor(d / 86400)}j`; })()}
                  </span>
                ) : null;
              })()}
            </div>
            <ActivityCount>{actions.length} événements</ActivityCount>
          </ActivityHeader>

          <ActivityList>
            {(() => {
              // Gmail actions only
              const unified = [...actions.map(a => ({ ...a, _service: 'gmail' }))].reverse();
              if (unified.length === 0) return (
                <EmptyState style={{ padding: 30 }}><Mail size={32} /><p>Aucune action enregistrée</p></EmptyState>
              );
              let lastDay = null; let dayIdx = 0;
              return unified.map((a, i) => {
                const actionDate = a.timestamp ? new Date(a.timestamp) : null;
                const dayKey = actionDate ? `${actionDate.getFullYear()}-${actionDate.getMonth()}-${actionDate.getDate()}` : null;
                let showDaySep = false;
                if (dayKey && dayKey !== lastDay) { showDaySep = true; if (lastDay !== null) dayIdx++; lastDay = dayKey; }
                const alt = dayIdx % 2 === 1;
                // Instagram actions
                if (a._service === 'instagram') {
                  const igMeta = IG_ACTION_MAP[a.type] || { label: a.type || 'Action', icon: Activity, color: '#E1306C', bg: 'rgba(225,48,108,.1)' };
                  const IgIcon = igMeta.icon;
                  const timeStr = a.timestamp ? new Date(a.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
                  const targetUser = a.target_user || '...';
                  return (
                    <React.Fragment key={`ig-${a.id || i}`}>
                      {showDaySep && (
                        <div style={{ padding: '4px 16px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-muted)', background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(() => { const d = new Date(a.timestamp); const now = new Date(); if (d.toDateString() === now.toDateString()) return "Aujourd'hui"; const y = new Date(now); y.setDate(y.getDate()-1); if (d.toDateString() === y.toDateString()) return 'Hier'; return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); })()}
                        </div>
                      )}
                      <ActivityItem $alt={alt}>
                        <div className="act-icon" style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: igMeta.bg, flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                          {igMeta.showProfilePic && targetUser !== '...' && (
                            <img
                              src={getLocalProfilePicUrl(targetUser)}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', position: 'absolute', top: 0, left: 0 }}
                              onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb='1'; e.target.src=`https://unavatar.io/instagram/${targetUser}`; } else e.target.style.display='none'; }}
                            />
                          )}
                          <IgIcon size={14} style={{ color: igMeta.color }} />
                        </div>
                        <div className="act-text" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: 'var(--text-secondary)' }}>
                          {igMeta.label} <strong style={{ fontWeight: 700, color: 'var(--text-primary)' }}>@{targetUser}</strong>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          {a.source && <span style={{ color: 'var(--primary)', fontSize: 10, fontWeight: 500, opacity: 0.7 }}>@{a.source}</span>}
                          <span className="act-time" style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 12 }}>{timeStr}</span>
                        </div>
                      </ActivityItem>
                    </React.Fragment>
                  );
                }
                // Gmail actions (original rendering)
                const cfg = ACTION_ICONS[a.action_type] || ACTION_ICONS.default;
                const AIcon = cfg.icon;
                const time = a.timestamp;
                const timeStr = time ? new Date(time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
                const statusColor = a.status === 'success' ? '#22c55e' : a.status === 'error' ? '#ef4444' : a.status === 'warning' ? '#f59e0b' : a.status === 'running' ? '#0ea5e9' : 'var(--text-muted)';
                const dataValue = a.data?.value || a.data?.email || '';
                return (
                  <React.Fragment key={a.id || i}>
                    {showDaySep && (
                      <div style={{ padding: '4px 16px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-muted)', background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 1 }}>
                        {(() => { const d = new Date(a.timestamp); const now = new Date(); if (d.toDateString() === now.toDateString()) return "Aujourd'hui"; const y = new Date(now); y.setDate(y.getDate()-1); if (d.toDateString() === y.toDateString()) return 'Hier'; return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); })()}
                      </div>
                    )}
                    <ActivityItem $alt={alt} style={{
                      borderLeft: `3px solid ${statusColor}`,
                      background: a.status === 'error' ? 'rgba(239,68,68,.04)' : a.status === 'success' && a.action_type === 'script_end' ? 'rgba(34,197,94,.04)' : undefined
                    }}>
                      <div className="act-icon" style={{
                        width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: `${cfg.color}15`, flexShrink: 0
                      }}>
                        {a.status === 'running' ? (
                          <RefreshCw size={12} style={{ color: '#0ea5e9', animation: 'spin 1.5s linear infinite' }} />
                        ) : (
                          <AIcon size={12} style={{ color: cfg.color }} />
                        )}
                      </div>
                      <div className="act-text" style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: 11, color: a.status === 'error' ? '#ef4444' : 'var(--text-primary)' }}>
                          {a.message}
                        </span>
                        {dataValue && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                            color: cfg.color, opacity: 0.85
                          }}>
                            {dataValue}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                        <span className="act-time">{timeStr}</span>
                        <span style={{
                          fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px',
                          color: statusColor, lineHeight: 1
                        }}>
                          {a.status === 'running' ? '● LIVE' : a.status === 'success' ? '✓' : a.status === 'error' ? '✗' : a.status === 'warning' ? '⚠' : ''}
                        </span>
                      </div>
                    </ActivityItem>
                  </React.Fragment>
                );
              });
            })()}
          </ActivityList>
        </ActivityPanel>
      </div>
      </ContentGrid>
    </Page>
  );
};

export default GmailAutomation;
